import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Users, MapPin, Trophy, FolderKanban, Award } from "lucide-react";

const REGIONS = [
  { value: "all", label: "Todas las zonas" },
  { value: "centro", label: "Zona Centro" },
  { value: "norte", label: "Zona Norte" },
  { value: "bajio", label: "Bajío" },
  { value: "otros", label: "Otros estados" },
];

const STAFF_TYPES = [
  { value: "all", label: "Todo el equipo" },
  { value: "obra", label: "Staff obra" },
  { value: "oficina", label: "Staff oficina central" },
];

export default function Employees() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStaff = searchParams.get("staff_type") || "all";
  const [filters, setFilters] = useState({ q: "", project_id: "all", region: "all", staff_type: initialStaff });
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      api.get("/employees", { params }).then(({ data }) => setEmployees(data)).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [filters]);

  // Keep URL param in sync so the filter is bookmarkable and reflects shortcut intent.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (filters.staff_type && filters.staff_type !== "all") next.set("staff_type", filters.staff_type);
    else next.delete("staff_type");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.staff_type]);

  const staffLabel = STAFF_TYPES.find((s) => s.value === filters.staff_type)?.label;

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#16A34A] font-bold flex items-center gap-1.5"><Users size={13} /> People</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tighter mt-1">Directorio de colaboradores.</h1>
        <p className="mt-2 text-sm text-[#5C6A7D] max-w-2xl">Perfiles profesionales del equipo. Conoce en qué proyectos participan, sus especialidades y su contribución a la sustentabilidad de la empresa.</p>
        {filters.staff_type !== "all" && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#16A34A]/10 text-[#166534] text-xs font-semibold" data-testid="staff-type-active-badge">
            Filtrando: {staffLabel}
            <button onClick={() => setFilters({ ...filters, staff_type: "all" })} className="ml-1 text-[#166534]/70 hover:text-[#166534]" data-testid="clear-staff-type-btn">×</button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E2E8F0] p-4 rounded-sm grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6A7D]" />
          <Input placeholder="Buscar por nombre…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="pl-9 rounded-sm border-[#E2E8F0] h-10" data-testid="employee-search-input" />
        </div>
        <Select value={filters.staff_type} onValueChange={(v) => setFilters({ ...filters, staff_type: v })}>
          <SelectTrigger className="rounded-sm h-10 border-[#E2E8F0]" data-testid="filter-emp-staff-type"><SelectValue placeholder="Tipo de staff" /></SelectTrigger>
          <SelectContent>{STAFF_TYPES.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`staff-type-opt-${s.value}`}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.region} onValueChange={(v) => setFilters({ ...filters, region: v })}>
          <SelectTrigger className="rounded-sm h-10 border-[#E2E8F0]" data-testid="filter-emp-region"><SelectValue placeholder="Zona" /></SelectTrigger>
          <SelectContent>{REGIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.project_id} onValueChange={(v) => setFilters({ ...filters, project_id: v })}>
          <SelectTrigger className="rounded-sm h-10 border-[#E2E8F0]" data-testid="filter-emp-project"><SelectValue placeholder="Proyecto" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos los proyectos</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-sm text-[#5C6A7D]">Cargando…</div>
      ) : employees.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-16 text-center text-sm text-[#5C6A7D]" data-testid="employees-empty"><Users className="mx-auto mb-3" size={28} strokeWidth={1.5} /> Ningún colaborador coincide con tu búsqueda.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="employees-grid">
          {employees.map((e) => (
            <button
              key={e.id}
              onClick={() => nav(`/employees/${e.id}`)}
              className="text-left bg-white border border-[#E2E8F0] rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(22,163,74,0.10)] hover:border-[#16A34A]/30"
              data-testid={`employee-card-${e.id}`}
            >
              <div className="flex items-center gap-4">
                <Avatar name={e.name} photo={e.photo} size={56} />
                <div className="min-w-0">
                  <div className="font-display font-bold text-[#0A2540] truncate">{e.name}</div>
                  <div className="text-xs text-[#5C6A7D]">{e.position || e.role_label}</div>
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-xs text-[#5C6A7D]">
                <div className="flex items-center gap-1.5"><FolderKanban size={13} className="text-[#2563EB]" /> {e.current_project_name || "Sin proyecto actual"}</div>
                {e.region && <div className="flex items-center gap-1.5"><MapPin size={13} className="text-[#16A34A]" /> {REGIONS.find((r) => r.value === e.region)?.label || e.region}</div>}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(e.specialties || []).slice(0, 3).map((s) => (
                  <span key={s} className="px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] font-semibold rounded-full bg-[#EFF5FF] text-[#1D4ED8]">{s}</span>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-[#E2E8F0] text-center">
                <div><div className="font-display font-extrabold text-[#0A2540]">{e.projects_count}</div><div className="text-[9px] uppercase tracking-[0.12em] text-[#5C6A7D]">Proyectos</div></div>
                <div><div className="font-display font-extrabold text-[#0A2540] flex items-center justify-center gap-0.5"><Trophy size={12} className="text-[#F59E0B]" />{e.total_points}</div><div className="text-[9px] uppercase tracking-[0.12em] text-[#5C6A7D]">Puntos</div></div>
                <div><div className="font-display font-extrabold text-[#0A2540] flex items-center justify-center gap-0.5"><Award size={12} className="text-[#16A34A]" />{e.badges_count}</div><div className="text-[9px] uppercase tracking-[0.12em] text-[#5C6A7D]">Insignias</div></div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
